import { Node, Edge } from 'reactflow';
import { NodeGroup } from '../types/groupTypes';

/**
 * Calcula el sequence_order de cada nodo basado en ordenamiento topológico.
 * Cada grupo tiene su propia numeración comenzando desde 1.
 * Respeta el orden de las conexiones en el canvas.
 */
export const calculateSequenceOrder = (
  edges: Edge[],
  groups: NodeGroup[]
): Map<string, number> => {
  const sequenceMap = new Map<string, number>();

  // Cada grupo tiene su propia secuencia independiente
  groups.forEach((group) => {
    if (group.nodeIds.length === 0) return;

    const groupNodeIds = new Set(group.nodeIds);
    
    // Crear mapa de adyacencia y grados de entrada
    const adjacencyMap = new Map<string, string[]>();
    const inDegree = new Map<string, number>();
    
    // Inicializar todos los nodos del grupo
    group.nodeIds.forEach(nodeId => {
      adjacencyMap.set(nodeId, []);
      inDegree.set(nodeId, 0);
    });
    
    // Construir grafo solo con edges dentro del grupo
    edges.forEach(edge => {
      if (groupNodeIds.has(edge.source) && groupNodeIds.has(edge.target)) {
        adjacencyMap.get(edge.source)?.push(edge.target);
        inDegree.set(edge.target, (inDegree.get(edge.target) || 0) + 1);
      }
    });
    
    // Encontrar TODOS los nodos raíz (sin edges entrantes dentro del grupo)
    const rootNodes: string[] = [];
    group.nodeIds.forEach(nodeId => {
      if ((inDegree.get(nodeId) || 0) === 0) {
        rootNodes.push(nodeId);
      }
    });
    
    // Si hay múltiples raíces, ordenarlas para procesamiento consistente
    rootNodes.sort();
    
    // Si no hay nodos raíz (ciclo), usar todos los nodos ordenados
    if (rootNodes.length === 0) {
      rootNodes.push(...[...group.nodeIds].sort());
    }
    
    // Topological sort (Kahn's algorithm) con secuencia POR GRUPO (comenzando desde 1)
    let groupSequence = 1;
    const processed = new Set<string>();
    const queue: string[] = [...rootNodes];
    
    while (queue.length > 0) {
      const currentNodeId = queue.shift()!;
      
      if (processed.has(currentNodeId)) continue;
      
      processed.add(currentNodeId);
      // Usar la secuencia del GRUPO (cada grupo empieza desde 1)
      sequenceMap.set(currentNodeId, groupSequence);
      groupSequence++;
      
      const children = adjacencyMap.get(currentNodeId) || [];
      const readyChildren: string[] = [];
      
      children.forEach(childId => {
        const currentInDegree = inDegree.get(childId) || 0;
        inDegree.set(childId, currentInDegree - 1);
        
        if (inDegree.get(childId) === 0 && !processed.has(childId)) {
          readyChildren.push(childId);
        }
      });
      
      readyChildren.sort();
      queue.push(...readyChildren);
    }
    
    // Asignar sequence a nodos no procesados (desconectados)
    const unprocessed = group.nodeIds.filter(id => !sequenceMap.has(id)).sort();
    unprocessed.forEach(nodeId => {
      sequenceMap.set(nodeId, groupSequence);
      groupSequence++;
    });
  });

  return sequenceMap;
};

/**
 * Actualiza todos los nodos con flow_id, group_id y sequence_order
 */
export const updateNodesWithFlowData = (
  nodes: Node[],
  edges: Edge[],
  groups: NodeGroup[],
  flowId: string | null
): Node[] => {
  const sequenceMap = calculateSequenceOrder(edges, groups);
  
  // Crear mapa de nodeId -> groupId
  // IMPORTANTE: usamos el NÚMERO secuencial del grupo (1, 2, 3...) como group_id,
  // NO el id interno (que puede ser un UUID cuando el flow se carga desde la BD).
  // La automatización externa filtra training_messages por flow_id + group_id numérico.
  const nodeToGroupMap = new Map<string, string>();
  groups.forEach(group => {
    group.nodeIds.forEach(nodeId => {
      nodeToGroupMap.set(nodeId, String(group.number));
    });
  });

  return nodes.map(node => ({
    ...node,
    data: {
      ...node.data,
      flowId: flowId,
      groupId: nodeToGroupMap.get(node.id) || null,
      sequenceOrder: sequenceMap.get(node.id) || 1,
    }
  }));
};

/**
 * Recalcula sequence_order después de eliminar un nodo
 */
export const recalculateAfterNodeDeletion = (
  nodes: Node[],
  edges: Edge[],
  groups: NodeGroup[],
  flowId: string | null
): Node[] => {
  return updateNodesWithFlowData(nodes, edges, groups, flowId);
};
